import { useEffect, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  Check,
  Image as ImageIcon,
  Link as LinkIcon,
  MapPin,
  MessageCircle,
  Upload,
  Users2,
  X,
} from 'lucide-react';
import { useAuth } from '@/app/providers';
import { Spinner } from '@/components/ui';
import { toast } from '@/components/feedback/toast';
import { ROUTES } from '@/config/constants';
import { initials } from '@/lib/utils';
import { ProfileBannerSurface } from '@/components/theme/ProfileBannerSurface';
import { gradientTextStyle } from '@/components/theme/CommunityThemeScope';
import { avatarFrameStyle } from '@/features/studio/model/profileDoc';
import { useProfileById } from '../hooks/useProfile';
import { useProfileTheme } from '../hooks/useProfileTheme';
import { useAddFriend, useFriendship, useMutualFriends, useSentFriendRequest } from '../hooks/useSocial';
import { getEmblem, getTitle } from '../cosmetics';
import { joinDate, parseLinks, stripHandle } from '../helpers';
import { EmblemGlyph } from './EmblemGlyph';
import { LinkFavicon, PlatformIcon, PLATFORM_LABEL } from '../socialPlatforms';

export function UserProfileModal({
  userId,
  anchorRect,
  onClose,
}: {
  userId: string | null;
  anchorRect?: DOMRect;
  onClose: () => void;
}) {
  const { user } = useAuth();
  const { data: profile, isLoading } = useProfileById(userId ?? undefined);
  const { data: friendship } = useFriendship(user?.id, userId ?? undefined);
  const { data: mutuals = [] } = useMutualFriends(user?.id, userId ?? undefined);
  const { data: requestSent } = useSentFriendRequest(user?.id, userId ?? undefined);
  const addFriend = useAddFriend(user?.id, userId ?? undefined);
  const profileTheme = useProfileTheme(
    userId,
    (profile ? getEmblem(profile.emblem_id) : null)?.glow ?? '#54b9fe',
  );

  useEffect(() => {
    if (!userId) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, userId]);

  if (!userId) return null;

  const title = profile ? getTitle(profile.title_id) : null;
  const emblem = profile ? getEmblem(profile.emblem_id) : null;
  const links = profile ? parseLinks(profile.links) : {};
  const linkEntries = Object.entries(links);
  const name = profile?.display_name || stripHandle(profile?.handle) || 'Member';
  const handle = stripHandle(profile?.handle);
  const tagline = profile?.headline || profile?.status || '';
  const showBio = profile?.bio && profile.bio !== tagline;
  const isSelf = Boolean(user && profile && user.id === profile.id);
  const popoverStyle = getPopoverStyle(anchorRect);
  const joined = joinDate(profile?.created_at);

  function shareProfile() {
    if (!handle) return;
    const url = `${window.location.origin}${ROUTES.profile(handle)}`;
    void navigator.clipboard?.writeText(url);
    toast('Profile link copied.');
  }

  function addAsFriend() {
    if (!user || !profile || isSelf || friendship || requestSent) return;
    addFriend.mutate(undefined, {
      onSuccess: () => toast('Friend request sent.'),
      onError: (err) => toast((err as Error).message || 'Could not send friend request', 'error'),
    });
  }

  return createPortal(
    <div className="fixed inset-0 z-[220]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="absolute w-[min(430px,calc(100vw-24px))] origin-top-left animate-fade-in-up overflow-y-auto rounded-2xl border border-white/10 bg-[rgba(13,14,20,0.68)] p-0 shadow-[0_30px_90px_rgba(0,0,0,0.55)] backdrop-blur-2xl backdrop-saturate-[1.35]"
        style={popoverStyle}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 z-10 grid h-8 w-8 place-items-center rounded-full bg-black/45 text-white/60 backdrop-blur-xl transition-colors hover:text-white"
        >
          <X size={16} />
        </button>

        {isLoading || !profile || !title || !emblem ? (
          <div className="grid h-64 place-items-center">
            {isLoading ? <Spinner /> : <span className="text-sm text-mid">Profile not found.</span>}
          </div>
        ) : (
          <div className="relative overflow-hidden">
          <div className="relative h-[178px] overflow-hidden">
            <ProfileBannerSurface theme={profileTheme} fallbackImage={title.bg} fallbackClassName="scale-105" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_10%,rgba(255,255,255,0.2),transparent_34%),linear-gradient(180deg,rgba(8,9,12,0.08),rgba(8,9,12,0.82))]" />
            {linkEntries.length > 0 && (
              <div className="absolute bottom-3 right-5 flex items-center gap-1.5">
                {linkEntries.slice(0, 5).map(([p, href]) => (
                  <a
                    key={p}
                    href={href}
                    target="_blank"
                    rel="noreferrer"
                    title={PLATFORM_LABEL[p] ?? p}
                    className="grid h-8 w-8 place-items-center rounded-lg bg-black/35 text-white/78 backdrop-blur-xl transition-all duration-200 hover:-translate-y-px hover:bg-white/10 hover:text-white"
                  >
                    {p === 'web' ? (
                      <span className="relative grid h-[17px] w-[17px] place-items-center overflow-hidden rounded-full">
                        <LinkFavicon href={href} />
                      </span>
                    ) : (
                      <PlatformIcon id={p} className="h-[17px] w-[17px]" />
                    )}
                  </a>
                ))}
              </div>
            )}
          </div>

          <div className="relative px-5 pb-5">
            <div className="-mt-12 flex items-end justify-between gap-3">
              <div
                className="grid h-[98px] w-[98px] flex-none place-items-center overflow-hidden rounded-full border-[4px] border-[#101016] bg-white/10 bg-cover bg-center text-2xl font-black text-white shadow-[0_16px_38px_rgba(0,0,0,0.62),0_0_0_1px_rgba(255,255,255,0.16)]"
                style={{
                  ...(profile.avatar_url
                    ? { backgroundImage: `url(${profile.avatar_url})` }
                    : { background: `linear-gradient(150deg, ${profileTheme.accent}, rgba(255,255,255,0.08))` }),
                  ...(profileTheme.doc.frame !== 'none'
                    ? avatarFrameStyle(profileTheme.doc.frame, profileTheme.accent)
                    : {}),
                }}
              >
                {!profile.avatar_url && initials(name)}
              </div>

              {handle && (
                <div className="mb-2 flex flex-none items-center gap-1.5">
                  <button
                    type="button"
                    onClick={shareProfile}
                    className="grid h-8 w-8 place-items-center rounded-lg text-accent transition-all duration-150 hover:-translate-y-px hover:bg-white/[0.06]"
                    title="Share profile"
                    aria-label="Share profile"
                  >
                    <Upload size={16} />
                  </button>
                  <Link
                    to={ROUTES.profile(handle)}
                    onClick={onClose}
                    className="grid h-8 w-8 place-items-center rounded-lg text-accent transition-all duration-150 hover:-translate-y-px hover:bg-white/[0.06]"
                    title="Message"
                    aria-label="Message"
                  >
                    <MessageCircle size={15} />
                  </Link>
                </div>
              )}
            </div>

            <div className="mt-3 min-w-0">
              <div className="flex min-w-0 items-center gap-1.5">
                <h2
                  className="truncate text-[24px] font-black leading-none tracking-[-0.5px] text-hi"
                  style={
                    profileTheme.doc.nameStyle === 'gradient'
                      ? gradientTextStyle(profileTheme.accent)
                      : undefined
                  }
                >
                  {name}
                </h2>
                {profileTheme.doc.showEmblem && <EmblemGlyph emblem={emblem} size={21} />}
              </div>
              {handle && <div className="mt-1 truncate text-[13px] font-semibold text-lo">@{handle}</div>}
              {joined && <div className="mt-1 text-[12px] font-semibold text-white/45">Joined WINR in {joined}</div>}
            </div>

            {tagline && <p className="mt-3 break-words text-[14px] font-semibold leading-relaxed text-white/70">{tagline}</p>}

            {showBio && (
              <p className="mt-3 max-h-24 overflow-y-auto whitespace-pre-wrap break-words rounded-lg border border-white/10 bg-white/[0.035] p-3 text-[13px] leading-relaxed text-mid">
                {profile.bio}
              </p>
            )}

            <div className="mt-4 rounded-lg bg-white/[0.045] p-3 backdrop-blur-xl">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2 text-[11px] font-black uppercase tracking-[0.7px] text-white/45">
                  <Users2 size={13} className="text-accent" />
                  Mutual connections
                </div>
                <div className="text-[11px] font-black text-white/45">{mutuals.length}</div>
              </div>
              {mutuals.length > 0 ? (
                <div className="mt-3 flex items-center gap-2">
                  <div className="flex -space-x-2">
                    {mutuals.slice(0, 5).map((m) => (
                      <span
                        key={m.id}
                        className="grid h-8 w-8 place-items-center rounded-full border-2 border-[#101016] bg-white/10 bg-cover bg-center text-[10px] font-black text-white"
                        style={m.avatar_url ? { backgroundImage: `url(${m.avatar_url})` } : undefined}
                        title={m.display_name ?? stripHandle(m.handle) ?? undefined}
                      >
                        {!m.avatar_url && initials(m.display_name || m.handle)}
                      </span>
                    ))}
                  </div>
                  <span className="min-w-0 truncate text-[12px] font-semibold text-white/55">
                    {mutuals.slice(0, 2).map((m) => m.display_name || stripHandle(m.handle)).join(', ')}
                    {mutuals.length > 2 ? ` +${mutuals.length - 2}` : ''}
                  </span>
                </div>
              ) : (
                <div className="mt-3 flex items-center gap-2 text-[12px] font-medium leading-relaxed text-white/40">
                  {friendship ? <ImageIcon size={14} /> : <LinkIcon size={14} />}
                  <span>{friendship ? 'Images and links you share will preview here.' : 'Nothing to display since you guys are not friends yet.'}</span>
                </div>
              )}
            </div>

            {profile.location && (
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px] text-lo">
                <span className="inline-flex items-center gap-1">
                  <MapPin size={12} /> {profile.location}
                </span>
              </div>
            )}

            {handle && (
              <div className="mt-4">
                {!friendship && !isSelf && requestSent ? (
                  <div className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.04] text-[13px] font-extrabold text-white/60">
                    <Check size={15} />
                    Request sent
                  </div>
                ) : !friendship && !isSelf ? (
                  <button
                    type="button"
                    onClick={addAsFriend}
                    disabled={addFriend.isPending}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-accent/12 text-[13px] font-extrabold text-accent transition-all duration-150 hover:bg-accent/18 disabled:opacity-60"
                  >
                    <Users2 size={15} />
                    {addFriend.isPending ? 'Sending request...' : 'Add friend'}
                  </button>
                ) : (
                  <Link
                    to={ROUTES.profile(handle)}
                    onClick={onClose}
                    className="inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] text-[13px] font-extrabold text-white/80 transition-all duration-150 hover:border-accent/35 hover:bg-accent/10 hover:text-accent"
                  >
                    View friendship <ArrowUpRight size={15} />
                  </Link>
                )}
              </div>
            )}
          </div>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}

function getPopoverStyle(anchor?: DOMRect): CSSProperties {
  const width = Math.min(430, window.innerWidth - 24);
  const gutter = 12;
  const maxHeight = Math.max(320, window.innerHeight - gutter * 2);
  const estimatedHeight = Math.min(560, maxHeight);
  if (!anchor) {
    return {
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%)',
      maxHeight,
    };
  }

  const rightFits = anchor.right + gutter + width <= window.innerWidth - gutter;
  const left = rightFits ? anchor.right + gutter : Math.max(gutter, anchor.left - width - gutter);
  const preferredTop = anchor.top + anchor.height / 2 - estimatedHeight / 2;
  const top = Math.min(Math.max(gutter, preferredTop), Math.max(gutter, window.innerHeight - estimatedHeight - gutter));
  return { left, top, maxHeight };
}
